import { Component, Input, OnInit } from "@angular/core";
import { AngularCsv, Options } from "angular-csv-ext/dist/Angular-csv";
import { Observable, Subscription } from "rxjs";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { LoadingService } from "../../../core/loader/loading.service";
import { AnyObject, DataToCsvExport, FileExport } from "../../types/app.type";
import { UserInfo } from "../../types/user-info";
import { NotificationService } from "../notification/notification.service";

const exportTypes = ["browser", "email"] as const;
type Output = typeof exportTypes[number];

type FormatDataFunction<T = unknown> = (contracts: PaginatedResponse<T>) => DataToCsvExport[];
export const MAX_ROWS_TO_EXPORT = 500;

@Component({
  selector: "app-export-file",
  templateUrl: "./export-file.component.html",
  styleUrls: ["./export-file.component.scss"],
})
export class ExportFileComponent implements OnInit {
  @Input() type: FileExport;
  @Input() output: Output;
  @Input() filename: string;
  @Input() totalDocs: number;
  @Input() disabled: boolean;
  /** @deprecated */
  @Input() getDataToExport: Observable<PaginatedResponse<unknown>>;
  /** @deprecated */
  @Input() getDataToExportQueue: Observable<unknown>;
  /** @deprecated */
  @Input() displayedHeaders: string[];
  /** @deprecated */
  @Input() formatData: FormatDataFunction;
  @Input() customCsvOptions: Partial<Omit<Options, "filename">>;
  /** Used to apply the new "downloadFile" method version. Remove after refactor */
  @Input() useNewMethod: boolean;
  @Input() exportToBrowser$: Observable<AnyObject<unknown>[]>;
  @Input() exportToEmail$: Observable<unknown>;
  currentUser: UserInfo;
  loading$ = this.loader.loading$;
  config: {
    csv: Partial<Options>;
    // other file types here...
  };

  constructor(
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private loader: LoadingService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  ngOnInit(): void {
    this.setDefaultCsvConfig();
  }

  // #region Handlers

  onDownloadFile(): void {
    if (this.useNewMethod) {
      this.downloadFile();
    } else {
      this.oldDownloadFile();
    }
  }

  // #endregion

  // #region Private Methods

  private setDefaultCsvConfig(): void {
    this.config = {
      ...this.config,
      csv: {
        fieldSeparator: ",",
        decimalseparator: ".",
        showLabels: true,
        useHeader: true,
        nullToEmptyString: true,
        ...this.customCsvOptions,
      },
    };
  }

  private downloadFile(): void {
    let subscription: Subscription = null;

    this.loader.show();

    if (FileExport.csv === this.type) {
      if (this.totalDocs <= MAX_ROWS_TO_EXPORT && this.output !== "email") {
        subscription = this.exportCsvToBrowser();
      } else if (this.output !== "browser") {
        subscription = this.exportCsvToEmail();
      }
    }

    subscription.add(() => this.loader.hide());
  }

  // #endregion

  /** Export CSV file to browser */
  private exportCsvToBrowser(): Subscription {
    return this.exportToBrowser$.subscribe((data) => {
      if (data?.length) {
        const headers = Object.keys(data[0]);
        this.updateCsvHeaders(headers);
        const parsedData = this.parseData(data);
        new AngularCsv(parsedData, this.filename, this.config.csv);
      } else {
        this.notificationService.error("No data to export");
      }
    }, this.handleError);
  }

  /** Export CSV file to email */
  private exportCsvToEmail(): Subscription {
    return this.exportToEmail$.subscribe(() => {
      this.notificationService.success("Your csv export is being processed, you will receive an email shortly");
    }, this.handleError);
  }

  /** Parse the csv data to be exported */
  private parseData(data: AnyObject[]): AnyObject[] {
    const maxLength = 50000; // limit is 50k (49.999) due a issue with google sheet/drive
    return data?.map((obj) => {
      const keys = Object.keys(obj);
      const parsedObj = {};
      for (const key of keys) {
        const value = obj[key];
        if (typeof value === "string") {
          // if the value is a string we have to:
          // 1) Escape thr "=" char
          // 2) Limit the string length
          parsedObj[key] = value.startsWith("=") ? `\\${value}` : value;
          parsedObj[key] = parsedObj[key].substring(0, maxLength - 1);
        } else {
          parsedObj[key] = value;
        }
      }
      return parsedObj;
    });
  }

  /** Update the "headers" parameter on the csv config. It's used to display the csv columns properly */
  private updateCsvHeaders(headers: string[]): void {
    this.config.csv = { ...this.config.csv, headers };
  }

  private handleError = (err: string) => {
    console.error(err);
    this.notificationService.error("Something went wrong. Try again later");
  };

  /** @deprecated - Remove after refactor */
  private oldDownloadFile(): void {
    this.loader.show();
    if (this.type === FileExport.csv && this.totalDocs <= MAX_ROWS_TO_EXPORT) {
      this.getDataToExport
        .subscribe(
          (data) => {
            const formatCsvData = this.formatData(data);
            if (formatCsvData.length) {
              new AngularCsv(formatCsvData, this.filename, this.config.csv);
            } else {
              this.notificationService.error("No data to export");
            }
          },
          () => {
            this.notificationService.error("Something went wrong. Try again later");
          },
        )
        .add(() => this.loader.hide());
    } else if (this.type === FileExport.csv && this.totalDocs > MAX_ROWS_TO_EXPORT) {
      this.getDataToExportQueue
        .subscribe(
          () => {
            this.notificationService.success("Your csv export is being processed, you will receive an email shortly");
          },
          () => {
            this.notificationService.error("Something went wrong. Try again later");
          },
        )
        .add(() => this.loader.hide());
    }
  }
}
